import { ReactNode } from 'react'
import styled from 'styled-components'

import useMediaQuery from '@/shared/hooks/useMediaQuery'
import usePageMatch from '@/shared/hooks/usePageMatch'
import { Nanum_r_size, Nanum_rb_size } from '@/styles/font/NanumSquare'
import { mediaMobile, mediaTablet } from '@/styles/mediaQuery'

const PageLayout = ({ children }: { children: ReactNode }) => {
  const { isHomePage, isIntroPage, isFaqPage, isCancelPage, isRefundPage } = usePageMatch()
  const isPageWithSubTitle = isIntroPage || isFaqPage || isCancelPage || isRefundPage
  const isCSPage = !!(isFaqPage || isRefundPage)

  const { isMobile } = useMediaQuery()

  const subTitleMeta = {
    intro: {
      title: `${
        isMobile
          ? '잃어버린 내 휴대폰을 빠르고\n 간편하게 찾을 수 있는 방법 없을까요?'
          : '잃어버린 내 휴대폰을 빠르고 간편하게\n 찾을 수 있는 방법 없을까요?'
      }`,
      description:
        '휴대폰을 잃어버린 경험이 있는 분들이라면 한 번쯤은 생각해 봤을 분실 시 있었으면 하는 기능들, 휴대폰분실보호에서 모두 이용하실 수 있습니다.',
    },
    faq: {
      title: `${
        isMobile
          ? '휴대폰분실보호 서비스를\n100% 활용하는 방법? 확인해 보세요.'
          : '휴대폰분실보호 서비스를 100% 활용하는 방법?\n확인해 보세요.'
      }`,
      description:
        'FAQ 목록 외에 더 궁금한 내용이 있으시다면 홈 > 고객센터 (1:1 문의)를 통해 문의사항을 접수해 주세요. 담당자 확인 후 즉시 답변해 드리겠습니다.',
    },
    cancel: {
      title: `서비스를 해지하기 전에\n아래 내용을 꼭 확인해 주세요.`,
      description: `
      <strong>
        * 서비스 해지 시 ‘휴대폰 위치 찾기’, ‘습득자와 영상통화’, ‘분실폰 잠금’ 등 휴대폰 분실보호에서 제공하는 회수 기능들을 더 이상 이용할 수 없습니다.<br/>
        * 백업 데이터는 서비스 해지와 동시에 즉시 삭제되어 복원이 어려우니 해지는 신중히 결정하시기 바랍니다.<br/>
      </strong>
      <span>
        * 가입 당일 해지하신 고객님은 요금이 발생하지 않습니다.<br/>
      </span>
      * 서비스 이용요금은 매월 사용일 수만큼 계산되어 청구됩니다.`,
    },
    refund: {
      title: `서비스 환불을 신청하기 전에\n아래 내용을 꼭 확인해 주세요.`,
      description: `
      <strong>
        * 서비스 이용 이력이 없을 경우 환불이 가능합니다.<br/>
      </strong>`,
    },
  }

  const subTitleData =
    isPageWithSubTitle && subTitleMeta[location.pathname.split('/')[1] as keyof typeof subTitleMeta]

  return (
    <PageLayoutWrap $isMain={!!isHomePage}>
      {subTitleData && (
        <SubTitleWrap $isCSPage={isCSPage}>
          <SubTitle>{subTitleData.title}</SubTitle>
          <SubDescription
            dangerouslySetInnerHTML={{ __html: subTitleData.description } as any}
            $hasMargin={!!(isIntroPage || isFaqPage)}
          />
        </SubTitleWrap>
      )}
      {children}
    </PageLayoutWrap>
  )
}

export default PageLayout

const PageLayoutWrap = styled.div<{ $isMain: boolean }>`
  margin: 0 auto;
  max-width: ${({ $isMain }) => ($isMain ? '1200px' : '1120px')};
  ${mediaTablet} {
    padding-left: 18%;
    padding-right: 18%;
  }
  ${mediaMobile} {
    padding-left: ${({ $isMain }) => ($isMain ? '0' : '24px')};
    padding-right: ${({ $isMain }) => ($isMain ? '0' : '24px')};
  }
`

const SubTitleWrap = styled.section<{ $isCSPage?: boolean }>`
  padding-top: ${({ $isCSPage }) => ($isCSPage ? '60px' : '52px')};
  padding-bottom: 25px;
  border-bottom: 1px solid var(--gray-be);
  margin-bottom: ${({ $isCSPage }) => ($isCSPage ? '0px' : '150px')};
  ${mediaTablet} {
    padding: ${({ $isCSPage }) => ($isCSPage ? '40px 0 55px 0' : '60px 0 50px 0')};
    margin-bottom: ${({ $isCSPage }) => ($isCSPage ? '0px' : '70px')};
  }
`
const SubTitle = styled.h3`
  ${Nanum_rb_size(32)}
  line-height: 1.2;
  margin-bottom: 50px;
  white-space: pre-line;

  ${mediaTablet} {
    ${Nanum_rb_size(20)};
    line-height: 1.6;
    margin-bottom: 16px;
  }
`
const SubDescription = styled.p<{ $hasMargin?: boolean }>`
  ${Nanum_r_size(18)};
  line-height: 1.6;
  color: var(--gray-28);
  margin-bottom: ${({ $hasMargin }) => ($hasMargin ? '50px' : 0)};

  strong {
    color: var(--red-2);
  }
  span {
    text-decoration: underline;
  }

  ${mediaTablet} {
    ${Nanum_r_size(14)}
    line-height: 1.6;
    margin-bottom: 0px;
  }
`
